<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 7/11/2021
  -->

<template>
	<div class="mb-5 genogram-main-page-root" v-bind="attrsStylesClass">
		<div v-if="genogramItem && !showEmptyGenogram">
			<slot name="empty">No Genogram Available</slot>
		</div>
		<div v-if="genogramItem && showEmptyGenogram" class="cy-editor relative">
			<CyEditorView
				ref="cyEditor"
				class="cy-editor"
				:client-profile="clientProfile"
				:embedded="embedded"
				:embedded-legend="embeddedLegendComputed"
				:readonly="readonly"
				:genogramItem="genogramItem"
				:customConnectionsSyncManager="customConnectionsSyncManager"
				:cy-config="cyConfig"
				:dataManagerProp="dataManager"
				:editor-config="editorConfig"
				@templateAdded="onTemplateAdded"
				@detailsVisibilityChanged="onDetailsVisibilityChanged"
				@change="change"
			/>
			<slot name="genogram-overlay"></slot>
		</div>

		<LoadingIndicator/>

	</div>
</template>

<script>

import BaseClientDetailsTab from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/BaseClientDetailsTab.vue";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import CyEditorView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/CyEditorView.vue";
import GenogramItem from "@shared/Genogram/GenogramItem";
import GenogramKidsTemplateDialog
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramKidsTemplateDialog.vue";
import {createDialog} from "@/utils/helpers-external";
import GenogramParentsTemplateDialog
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramParentsTemplateDialog.vue";
import {messageDialog, yesNoDialog} from "@/core/components/Dialogs/dialog";
import {asArray} from "@/utils/helpers";
import {
	applyHandwritingSpaceToGenogramItem,
	revertHandwritingSpaceFromGenogramItem
} from "@/tools/GenogramTool/lib/GenogramHandwritingSpaceHelper";
import {GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} from "@/core/js/model/SharedConstants";
import GenogramCustomConnectionsSyncManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramCustomConnectionsSyncManager";
import GenogramAnalyticsSignalManager
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramAnalyticsSignalManager";

export default {
	name: "GenogramMain",
	components: {GenogramParentsTemplateDialog, GenogramKidsTemplateDialog, CyEditorView, LoadingIndicator},
	extends: BaseClientDetailsTab,

	inheritsAttrs: false,

	props: {
		genogramItemProp: GenogramItem,
		hideSaveButton: Boolean,
		showWatermark: Boolean,
		actionListener: {Function, default: () => {}},
		embedded: Boolean,
		embeddedLegend: {type: Boolean, default: undefined},
		readonly: Boolean,
		legendBottom: String,
		canShowEmptyGenogram: {Boolean, default: true},
		config: {Object, default: () => ({})},
		analyticsContext: {type: Object, default: () => ({})},
		analyticsSignalManager: {type: GenogramAnalyticsSignalManager, default: null},
	},


	data() {
		return {
			isLoaded: false,
			genogramItem: null,
			customConnectionsSyncManager: null,
			analyticsSignalManagerInternal: null,
			isExistingGenogramAnalytics: false,
			cyConfig: {},
			editorConfig: {
				isLoggedIn: this.$authManager.isLoggedIn(),
				hideSaveButton: this.hideSaveButton,
				showWatermark: this.showWatermark,
				actionListener: this.actionListenerDefault,
				lineType: 'straight'
			}
		}
	},

		created() {
			this.analyticsSignalManagerInternal = this.analyticsSignalManager || new GenogramAnalyticsSignalManager(this.$analyticManager)
			this.customConnectionsSyncManager = new GenogramCustomConnectionsSyncManager({
				dataManager: this.dataManager,
			})
		this.editorConfig = {
			...this.editorConfig,
			...this.config,
		}
		if(this.shouldHideEditorChrome){
			this.editorConfig = {
				...this.editorConfig,
				toolbar: false,
				dragAddNodes: false,
				elementsInfo: false,
			}
		}
			if(this.readonly){
				let toolbar = this.shouldHideEditorChrome ? false : ['zoomin', 'zoomout', 'fit']
				this.editorConfig = {
					...this.editorConfig,
					toolbar,
					dragAddNodes: false,
					nodeContextMenu: {
						enabled: false,
					},
			}
		}
		if(this.legendBottom){
			this.cyConfig.legendBottom = this.legendBottom
		}
		this.loadGenogram()
	},

	watch: {
		"config.showHandwritingSpace"(nextValue, previousValue) {
			if (nextValue === previousValue || !this.genogramItem) {
				return
			}

			this.updateHandwritingMode(nextValue)
		},
	},


	methods: {
		setGenogramData(genogramData, idsToDelete){
			console.log("GenogramMain.setGenogramData genogramData = ", genogramData, ", idsToDelete = ", idsToDelete)

			this.$refs.cyEditor.getCyEditor().deleteByIds(idsToDelete)
			this.$refs.cyEditor.getCyEditor().addOrUpdateElements(genogramData)
		},

		async actionListenerDefault({action}, params){
			let res = await this.actionListener({action}, params)

			console.log("GenogramMain.actionListenerDefault action = ", action, ", res = ", res)

			if(res !== undefined){
				return res
			}

			switch (action){
				case 'analyticsExportCompleted':
					this.trackExportAnalytics(params)
					return true
				case 'getTemplateGenerationsParams':
					return this.getTemplateGenerationsParams(params)
				case 'getTemplateParentsParams':
					return this.getTemplateParentsParams(params)
				case 'getTemplateCoupleParams':
					return this.getTemplateCoupleParams(params)
				case 'getTemplateKidsParams':
					return this.getTemplateKidsParams(params)
				case 'showMessageYesNo':
					return yesNoDialog(params)
				case 'showMessage':
					return messageDialog(params)
			}
		},

		async notifyTemplateRequested(){
			await this.emitPromise("templateAdded")
		},

		async getTemplateGenerationsParams(params = {}){
			await this.notifyTemplateRequested()
			let genogramParentsTemplateDialog = createDialog(GenogramParentsTemplateDialog)
			return genogramParentsTemplateDialog(params)
		},
		async getTemplateParentsParams(params = {}){
			await this.notifyTemplateRequested()
			let genogramParentsTemplateDialog = createDialog(GenogramParentsTemplateDialog)
			return genogramParentsTemplateDialog(params)
		},

		async getTemplateCoupleParams(params = {}){
			await this.notifyTemplateRequested()
			let {templateType} = params
			let genogramKidsTemplateDialog = createDialog(GenogramKidsTemplateDialog)
			return genogramKidsTemplateDialog({
				templateType
			})
		},

		async getTemplateKidsParams(params = {}){
			await this.notifyTemplateRequested()
			let {existingPartners, templateType} = params
			let genogramKidsTemplateDialog = createDialog(GenogramKidsTemplateDialog)
			return genogramKidsTemplateDialog({
				existingPartners,
				templateType,
				showPartnerSelect: true,
			})
		},

		async loadGenogram() {
			await this.customConnectionsSyncManager.loadDefinitions()

			let res
			let isExistingGenogram = false
			if (this.genogramItemProp) {
				res = this.genogramItemProp
				isExistingGenogram = this.analyticsContext.is_existing_genogram === true
			} else {
				res = await this.handleAsync(async () => {
					let genogramItem = await this.dataManager.getGenogramByClientId(this.clientProfile.id)
					isExistingGenogram = !!genogramItem
					if(!genogramItem){
						genogramItem = new GenogramItem(this.clientProfile.id)
						genogramItem.addSharedWithById([this.userProfile.id, this.clientProfile.id])
					}
					return genogramItem
				})
			}

			if (res) {
				this.isExistingGenogramAnalytics = isExistingGenogram
				this.customConnectionsSyncManager.syncGenogramItem(res)
				this.applyGenogramItem(res)
				this.trackSessionStartAnalytics()
			}
			console.log("this.genogramItem = ", this.genogramItem)
		},

		applyGenogramItem(genogramItem) {
			this.genogramItem = this.prepareGenogramItemForDisplay(genogramItem)
			this.refreshCyEditorGenogramItem()
		},

		prepareGenogramItemForDisplay(genogramItem) {
			if (!this.config?.showHandwritingSpace) {
				return genogramItem
			}

			let genogramItemClone = new GenogramItem(genogramItem.clientId)
			Object.assign(genogramItemClone, JSON.parse(JSON.stringify(genogramItem || {})))
			applyHandwritingSpaceToGenogramItem(genogramItemClone)
			return genogramItemClone
		},

		updateHandwritingMode(isEnabled) {
			let currentGenogramItem = this.getCurrentGenogramItem()
			if (!currentGenogramItem) {
				return
			}

			let nextGenogramItem = new GenogramItem(currentGenogramItem.clientId)
			Object.assign(nextGenogramItem, JSON.parse(JSON.stringify(currentGenogramItem || {})))

			if (isEnabled) {
				if (!this.hasHandwritingNotes(nextGenogramItem)) {
					applyHandwritingSpaceToGenogramItem(nextGenogramItem)
				}
			} else if (this.hasHandwritingNotes(nextGenogramItem)) {
				revertHandwritingSpaceFromGenogramItem(nextGenogramItem)
			}

			this.genogramItem = nextGenogramItem
			this.replaceCyEditorGenogramItem()
		},

		getCurrentGenogramItem() {
			let cyEditor = this.$refs.cyEditor?.getCyEditor?.()
			if (cyEditor && this.genogramItem) {
				this.genogramItem.jsonData = cyEditor.json()
			}

			return this.genogramItem
		},

		hasHandwritingNotes(genogramItem) {
			let nodes = genogramItem?.jsonData?.elements?.nodes || []
			return nodes.some(node => node?.data?.isHandwritingSpaceNote)
		},

		refreshCyEditorGenogramItem() {
			let cyEditor = this.$refs.cyEditor?.getCyEditor?.()
			if (!cyEditor || !this.genogramItem?.jsonData) {
				return
			}

			cyEditor.setGenogramItem(this.genogramItem)
			cyEditor.json({
				...this.genogramItem.jsonData,
				userPanningEnabled: true,
				userZoomingEnabled: true,
				panningEnabled: true,
				zoomingEnabled: true,
			})
		},

		replaceCyEditorGenogramItem() {
			let cyEditor = this.$refs.cyEditor?.getCyEditor?.()
			let cy = cyEditor?.cy
			if (!cyEditor || !cy || !this.genogramItem?.jsonData) {
				return
			}

			cy.elements().remove()
			cyEditor.setGenogramItem(this.genogramItem)
			cyEditor.json({
				...this.genogramItem.jsonData,
				userPanningEnabled: true,
				userZoomingEnabled: true,
				panningEnabled: true,
				zoomingEnabled: true,
			})
			cyEditor.invalidateLabelFontSize()
		},

		async change() {
			this.customConnectionsSyncManager.syncGenogramItem(this.genogramItem)
			await this.handleAsyncNonBlocking(() => this.dataManager.setGenogramItem(this.genogramItem))
			let analyticsSignalProps = this.buildAnalyticsSignalProps()
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramMain: change tracking input`, analyticsSignalProps)
			let meaningfulUsageSignal = this.analyticsSignalManagerResolved.reportEditingActivity(analyticsSignalProps)
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramMain: change processed`, {
				genogramId: meaningfulUsageSignal?.genogram_id || analyticsSignalProps?.genogramItem?.id || analyticsSignalProps?.genogramItem?.clientId,
				analyticsSignalProps,
				hasMeaningfulUsageSignal: !!meaningfulUsageSignal,
				meaningfulUsageSignal,
			})
			await this.emitMeaningfulUsageDetectedIfNeeded(meaningfulUsageSignal)
			// console.log("Genogram.change this.genogramItem: ", JSON.stringify(this.genogramItem))
			console.log("Genogram.change this.genogramItem: ", this.genogramItem)
			// await this.$analyticManager.trackEvent("Genogram Changed")
			return this.emitPromise("change", this.buildAnalyticsSignalProps())
		},

		async emitMeaningfulUsageDetectedIfNeeded(meaningfulUsageSignal = null) {
			if(!meaningfulUsageSignal){
				return
			}
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramMain: emitting meaningfulUsageDetected`, meaningfulUsageSignal)
			await this.emitPromise("meaningfulUsageDetected", meaningfulUsageSignal)
		},

		trackSessionStartAnalytics() {
			this.analyticsSignalManagerResolved.startWorkPeriod(this.buildAnalyticsBaseContext())
			this.analyticsSignalManagerResolved.trackSessionStarted(this.buildAnalyticsSignalProps())
		},

		trackSaveAnalytics(options = {}) {
			this.analyticsSignalManagerResolved.trackSaved(this.buildAnalyticsSignalProps(options))
		},

		trackExportAnalytics(options = {}) {
			this.analyticsSignalManagerResolved.trackExported(this.buildAnalyticsSignalProps({
				export_format: options?.format,
			}))
		},

		buildAnalyticsSignalProps(extraProps = {}) {
			let genogramItem = this.getCurrentGenogramItem() || this.genogramItem
			return {
				genogramItem,
				is_existing_genogram: this.isExistingGenogramComputed,
				open_source: this.openSourceComputed,
				...extraProps,
			}
		},

		buildAnalyticsBaseContext() {
			let {
				...analyticsContext
			} = this.analyticsContext || {}
			return {
				entry_point: this.entryPointComputed,
				is_embedded_mode: this.embedded,
				is_readonly_mode: this.readonly,
				is_logged_in: this.$authManager.isLoggedIn(),
				...analyticsContext,
			}
		},

		async onDetailsVisibilityChanged(isVisible) {
			return this.emitPromise('detailsVisibilityChanged', isVisible)
		},

		async onTemplateAdded() {
			return this.emitPromise('templateAdded')
		}
	},

	computed: {
		analyticsSignalManagerResolved() {
			return this.analyticsSignalManagerInternal
		},

		entryPointComputed() {
			return this.analyticsContext.entry_point || "client_record"
		},

		isExistingGenogramComputed() {
			if(this.analyticsContext.is_existing_genogram != null){
				return this.analyticsContext.is_existing_genogram
			}
			return this.isExistingGenogramAnalytics
		},

		openSourceComputed() {
			return this.analyticsContext.open_source || (this.isExistingGenogramComputed ? "account_existing" : "new")
		},

		embeddedLegendComputed() {
			if (this.embeddedLegend !== undefined) {
				return this.embeddedLegend
			}

			return this.embedded
		},

		shouldHideEditorChrome() {
			return this.embedded
		},

		showEmptyGenogram() {
			let elements = {}
			if (this.genogramItem) {
				elements = (this.genogramItem.jsonData || {}).elements || {}
			}

			let isGenogramEmpty = Object.keys(elements).length === 0
			return !isGenogramEmpty || this.canShowEmptyGenogram
		}
	},
}
</script>

<style lang="scss">

</style>
<style lang="scss">
.genogram-main-page-root {
	.cy-editor {
		width: 100%;
		height: 100%;
		min-height: 300px;
		position: relative;


	}
}
</style>
